import { useEffect, useState, useCallback } from "react";
import { Helmet } from "react-helmet-async";
import {
  Box,
  Card,
  Container,
  Typography,
  Switch,
  Divider,
  Stack,
  Button,
  Breadcrumbs,
  CircularProgress,
  Alert,
  Chip,
} from "@mui/material";
import { HomeRounded, NotificationsOutlined, EmailOutlined, CircleNotificationsOutlined } from "@mui/icons-material";
import { Link } from "react-router-dom";
import { showSuccessToast, showErrorToast } from "../../utils/Toast";
import ApiCall from "../../utils/apicall";

// ── Event groups for UI section rendering ───────────────────────────────────
const EVENT_GROUPS = [
  {
    label: "QA / NCR",
    color: "#C00000",
    types: ["ncr_raised", "ncr_status_changed", "ncr_closed", "tsi_gate_not_run"],
  },
  {
    label: "Tasks",
    color: "#2E75B6",
    types: ["task_assigned", "task_approved", "task_rejected"],
  },
  {
    label: "Heat Management",
    color: "#375623",
    types: ["heat_cmtr_verified", "heat_released"],
  },
  {
    label: "Release Packages",
    color: "#7D6608",
    types: ["release_package_activated", "release_compliance"],
  },
  {
    label: "Vendors / AVL",
    color: "#5C3D1E",
    types: ["vendor_avl_approved", "vendor_suspended"],
  },
  {
    label: "WeldAssure",
    color: "#7A4DB5",
    types: ["wps_code_mismatch"],
  },
  {
    label: "Operations",
    color: "#444444",
    types: ["bol_created", "qc_hold_active", "job_status_changed"],
  },
];

// ── Priority tags ────────────────────────────────────────────────────────────
const HIGH_PRIORITY = new Set([
  "ncr_raised",
  "ncr_closed",
  "task_rejected",
  "release_package_activated",
  "vendor_avl_approved",
  "vendor_suspended",
  "qc_hold_active",
  "tsi_gate_not_run",
  "wps_code_mismatch",
  "release_compliance",
]);

// Auth handled by ApiCall interceptor

export default function NotificationSettings() {
  const [settings, setSettings]   = useState([]);   // Array of { eventType, label, inAppEnabled, emailEnabled }
  const [loading, setLoading]     = useState(true);
  const [saving, setSaving]       = useState(false);
  const [error, setError]         = useState(null);
  const [dirty, setDirty]         = useState(false);

  // Load current settings
  const loadSettings = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await ApiCall.get("/notifications/settings");
      setSettings(res.data?.data?.settings || []);
    } catch (err) {
      console.error(err);
      setError("Could not load notification settings. Please refresh and try again.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  // Toggle a single field
  const toggle = (eventType, field) => {
    setSettings((prev) =>
      prev.map((s) =>
        s.eventType === eventType ? { ...s, [field]: !s[field] } : s
      )
    );
    setDirty(true);
  };

  // Toggle ALL in-app or ALL email for a group
  const toggleGroup = (types, field, value) => {
    setSettings((prev) =>
      prev.map((s) =>
        types.includes(s.eventType) ? { ...s, [field]: value } : s
      )
    );
    setDirty(true);
  };

  // Enable / disable everything
  const toggleAll = (field, value) => {
    setSettings((prev) => prev.map((s) => ({ ...s, [field]: value })));
    setDirty(true);
  };

  // Save
  const save = async () => {
    setSaving(true);
    try {
      await ApiCall.put(
        "/notifications/settings",
        { settings: settings.map(({ eventType, inAppEnabled, emailEnabled }) => ({ eventType, inAppEnabled, emailEnabled })) }
      );
      showSuccessToast("Notification settings saved.");
      setDirty(false);
    } catch (err) {
      console.error(err);
      showErrorToast("Failed to save settings. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  // ── Build a lookup map for quick access ─────────────────────────────────
  const settingsMap = {};
  settings.forEach((s) => {
    settingsMap[s.eventType] = s;
  });

  return (
    <>
      <Helmet>
        <title>Notification Settings | SFM</title>
      </Helmet>

      <Container maxWidth="md">
        {/* Breadcrumbs */}
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
          <Breadcrumbs separator="›" aria-label="breadcrumb">
            <Link to="/dashboard/app" style={{ display: "flex", alignItems: "center", color: "inherit", textDecoration: "none" }}>
              <HomeRounded fontSize="small" sx={{ mr: 0.5 }} />
              Home
            </Link>
            <Typography color="text.primary">Notification Settings</Typography>
          </Breadcrumbs>
        </Stack>

        {/* Page title */}
        <Stack direction="row" alignItems="center" spacing={1.5} mb={3}>
          <CircleNotificationsOutlined sx={{ fontSize: 32, color: "#1F4E79" }} />
          <Box>
            <Typography variant="h5" fontWeight={700} color="#1F4E79">
              Notification Settings
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Control which events send you in-app bell notifications and email alerts.
            </Typography>
          </Box>
        </Stack>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box display="flex" justifyContent="center" py={6}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {/* Global toggles */}
            <Card sx={{ p: 2.5, mb: 3, border: "1px solid #e0e0e0" }}>
              <Typography variant="subtitle1" fontWeight={700} mb={1.5}>
                Global Controls
              </Typography>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                <Stack spacing={0.5}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <NotificationsOutlined sx={{ color: "#1F4E79", fontSize: 18 }} />
                    <Typography variant="body2" fontWeight={600}>In-App Bell</Typography>
                  </Stack>
                  <Stack direction="row" spacing={1}>
                    <Button size="small" variant="outlined" onClick={() => toggleAll("inAppEnabled", true)}>Enable All</Button>
                    <Button size="small" variant="outlined" color="error" onClick={() => toggleAll("inAppEnabled", false)}>Disable All</Button>
                  </Stack>
                </Stack>
                <Divider orientation="vertical" flexItem sx={{ display: { xs: "none", sm: "flex" } }} />
                <Stack spacing={0.5}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <EmailOutlined sx={{ color: "#2E75B6", fontSize: 18 }} />
                    <Typography variant="body2" fontWeight={600}>Email</Typography>
                  </Stack>
                  <Stack direction="row" spacing={1}>
                    <Button size="small" variant="outlined" onClick={() => toggleAll("emailEnabled", true)}>Enable All</Button>
                    <Button size="small" variant="outlined" color="error" onClick={() => toggleAll("emailEnabled", false)}>Disable All</Button>
                  </Stack>
                </Stack>
              </Stack>
            </Card>

            {/* Event groups */}
            {EVENT_GROUPS.map((group) => {
              const groupSettings = group.types
                .map((t) => settingsMap[t])
                .filter(Boolean);

              if (groupSettings.length === 0) return null;

              const allInApp  = groupSettings.every((s) => s.inAppEnabled);
              const allEmail  = groupSettings.every((s) => s.emailEnabled);

              return (
                <Card key={group.label} sx={{ mb: 2, border: "1px solid #e0e0e0" }}>
                  {/* Group header */}
                  <Box
                    sx={{
                      px: 2.5, py: 1.5,
                      borderLeft: `4px solid ${group.color}`,
                      bgcolor: "#FAFAFA",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                  >
                    <Typography variant="subtitle2" fontWeight={700} color={group.color}>
                      {group.label}
                    </Typography>
                    {/* Group-level quick toggles */}
                    <Stack direction="row" spacing={2} alignItems="center">
                      <Stack direction="row" alignItems="center" spacing={0.5}>
                        <NotificationsOutlined sx={{ fontSize: 16, color: "#666" }} />
                        <Typography variant="caption" color="text.secondary">All</Typography>
                        <Switch
                          size="small"
                          checked={allInApp}
                          onChange={() => toggleGroup(group.types, "inAppEnabled", !allInApp)}
                        />
                      </Stack>
                      <Stack direction="row" alignItems="center" spacing={0.5}>
                        <EmailOutlined sx={{ fontSize: 16, color: "#666" }} />
                        <Typography variant="caption" color="text.secondary">All</Typography>
                        <Switch
                          size="small"
                          checked={allEmail}
                          onChange={() => toggleGroup(group.types, "emailEnabled", !allEmail)}
                        />
                      </Stack>
                    </Stack>
                  </Box>

                  {/* Individual event rows */}
                  {groupSettings.map((s, idx) => (
                    <Box key={s.eventType}>
                      <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        sx={{ px: 2.5, py: 1.25 }}
                      >
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Typography variant="body2">{s.label}</Typography>
                          {HIGH_PRIORITY.has(s.eventType) && (
                            <Chip
                              label="HIGH"
                              size="small"
                              sx={{
                                height: 18,
                                fontSize: "0.65rem",
                                fontWeight: 700,
                                bgcolor: "#FCE4D6",
                                color: "#C00000",
                              }}
                            />
                          )}
                        </Stack>
                        <Stack direction="row" spacing={3} alignItems="center">
                          {/* In-app toggle */}
                          <Stack direction="row" alignItems="center" spacing={0.5}>
                            <NotificationsOutlined
                              sx={{ fontSize: 16, color: s.inAppEnabled ? "#1F4E79" : "#ccc" }}
                            />
                            <Switch
                              size="small"
                              checked={!!s.inAppEnabled}
                              onChange={() => toggle(s.eventType, "inAppEnabled")}
                              color="primary"
                            />
                          </Stack>
                          {/* Email toggle */}
                          <Stack direction="row" alignItems="center" spacing={0.5}>
                            <EmailOutlined
                              sx={{ fontSize: 16, color: s.emailEnabled ? "#2E75B6" : "#ccc" }}
                            />
                            <Switch
                              size="small"
                              checked={!!s.emailEnabled}
                              onChange={() => toggle(s.eventType, "emailEnabled")}
                              color="info"
                            />
                          </Stack>
                        </Stack>
                      </Stack>
                      {idx < groupSettings.length - 1 && (
                        <Divider sx={{ mx: 2.5 }} />
                      )}
                    </Box>
                  ))}
                </Card>
              );
            })}

            {/* Legend */}
            <Stack direction="row" spacing={3} mb={3} alignItems="center">
              <Stack direction="row" spacing={0.5} alignItems="center">
                <NotificationsOutlined sx={{ fontSize: 16, color: "#1F4E79" }} />
                <Typography variant="caption" color="text.secondary">= In-app bell notification</Typography>
              </Stack>
              <Stack direction="row" spacing={0.5} alignItems="center">
                <EmailOutlined sx={{ fontSize: 16, color: "#2E75B6" }} />
                <Typography variant="caption" color="text.secondary">= Email notification</Typography>
              </Stack>
            </Stack>

            {/* Save button */}
            <Box display="flex" justifyContent="flex-end" pb={4}>
              <Button
                variant="contained"
                size="large"
                disabled={!dirty || saving}
                onClick={save}
                startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
                sx={{ bgcolor: "#1F4E79", "&:hover": { bgcolor: "#173D62" }, minWidth: 160 }}
              >
                {saving ? "Saving..." : "Save Settings"}
              </Button>
            </Box>
          </>
        )}
      </Container>
    </>
  );
}
